import { QueryClient, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import { useCallback, useRef } from 'react';

import {
    groupHeldPeriods,
    includesPeriod,
    periodsNotHeld,
} from 'modules/common/components/VerifiedStatementUpload/utils/periods';
import { fileHoldings } from 'modules/common/components/VerifiedStatementUpload/utils/removalOutcome';
import { removalNotice } from 'modules/common/components/VerifiedStatementUpload/utils/statementFilings';
import { Document } from 'modules/common/interfaces/documents';
import { useMember } from 'modules/common/queries/member';
import {
    AccountStatementCoverage,
    MemberStatementCoverage,
    STATEMENT_COVERAGE_QUERY_KEY,
    StatementPeriod,
} from 'services/bookkeeping/queries/useStatementCoverage';
import useAssignStatementAccount from 'services/bookkeeping/mutations/useAssignStatementAccount';
import useReassignStatementPeriods, {
    StatementPeriodAssignment,
    useRemoveStatementTracking,
} from 'services/bookkeeping/mutations/useReassignStatementPeriods';
import {
    STATEMENT_UPLOAD_STATUSES_QUERY_KEY,
    StatementUploadStatuses,
} from 'services/bookkeeping/queries/useStatementUploadStatuses';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import extractApiErrorMessage from 'utils/extractApiErrorMessage';

/**
 * Copy for the failures the member can retry out of, and which are otherwise invisible. Each says
 * what failed, not merely that something did.
 */
export const SAVE_FAILED_MESSAGE = "We couldn't save your changes just now. Please try again.";
export const DELETE_FAILED_MESSAGE = "We couldn't remove that file just now. Please try again.";

/**
 * A 409 removal refusal that arrived without a reason — otherwise the server's own is shown (see
 * `refusalMessage`). True of both causes: transactions already in the books, or a bank-delivered row.
 */
export const DELETE_BLOCKED_MESSAGE =
    "We couldn't remove that file — the months it covers can't be changed here. Please contact support if you need to update them.";

/**
 * Confirms the one action in this flow that is otherwise silent.
 *
 * Uploads and deletes announce themselves — a row appears or vanishes. A month correction
 * that was already showing on screen while staged changes nothing visible when it lands, so
 * without this the member cannot tell a save from a no-op.
 */
export const PERIODS_SAVED_MESSAGE = 'Your statement months were saved.';

/** Matches the snackbar dwell used by the other write failures in this flow. */
export const ERROR_SNACKBAR_DURATION_MS = 5000;
/** Shorter than the error dwell — a confirmation has nothing to be read twice. */
const SUCCESS_SNACKBAR_DURATION_MS = 3000;

/** Every refusal these endpoints raise shares one status, and they all mean the same to a caller. */
const HTTP_CONFLICT = 409;

function isRefusal(error: unknown): boolean {
    return isAxiosError(error) && error.response?.status === HTTP_CONFLICT;
}

/**
 * A refusal's own reason, which names the account and month, else `fallback`. Only a 409: any
 * other failure's body may be a validation dump or an HTML page, not copy for the member.
 */
function refusalMessage(error: unknown, fallback: string): string {
    return isRefusal(error) ? extractApiErrorMessage(error, ['error'], fallback) : fallback;
}

/** The member's document lists, which drop a file once the server has deleted it. */
const DOCUMENTS_QUERY_KEY = ['documents'];

/** A document as the cached document lists have it, or undefined when none lists it. */
function cachedDocument(queryClient: QueryClient, documentUuid: string): Document | undefined {
    return queryClient
        .getQueriesData<Document[]>({ queryKey: DOCUMENTS_QUERY_KEY })
        .flatMap(([, documents]) => (Array.isArray(documents) ? documents : []))
        .find((document) => document?.uuid === documentUuid);
}

/** The card a removal is made from — the subset of an account it needs. */
type RemovalAccount = Pick<AccountStatementCoverage, 'bank_account_id' | 'account_mask'>;
/** A card's id and the months coverage last reported it holding. */
type HeldAccount = Pick<AccountStatementCoverage, 'bank_account_id'> &
    Partial<Pick<AccountStatementCoverage, 'held_periods'>>;
/** Per-document outcome of a filing batch, so each caller can apply its own failure policy. */
interface FileDocumentsResult {
    readonly recorded: string[];
    readonly failed: string[];
}

interface StatementDocumentWrites {
    /** Send a session's month corrections as one request. Resolves whether it landed. */
    savePeriods: (
        assignments: StatementPeriodAssignment[],
        bankAccountId: string | null | undefined
    ) => Promise<boolean>;
    /**
     * Take a file off one card (one month, or all of it), say what that did, and refresh. Resolves
     * whether it landed, once coverage, the statuses and the document lists have refetched.
     */
    removeFromAccount: (documentUuid: string, account: RemovalAccount, period?: StatementPeriod) => Promise<boolean>;
    /**
     * Whether a file still holds a month on a card once `removed` came off it (none when absent).
     *
     * Read by the rule the remove control's label uses — coverage and the statuses together — and
     * from the coverage `removeFromAccount` just refetched, not the caller's click-time copy, so the
     * two can't disagree. The copy is only the fallback for a cache with nothing in it. This
     * session's removals are subtracted until neither read lists them, for a refetch that failed.
     */
    holdsMonthAfterRemoval: (documentUuid: string, account: HeldAccount, removed?: StatementPeriod) => boolean;
    /** File each document against a bank account. Reports per-document outcomes; announces nothing. */
    fileDocuments: (documentUuids: string[], bankAccountId: string | null | undefined) => Promise<FileDocumentsResult>;
}

/**
 * The statement writes that need no local store — the three every surface uploading statements
 * makes, with the request, the cache invalidation and what the member sees.
 *
 * Split out of `useStatementStepWrites`, which is welded to the onboarding step blob (hence its
 * write queue and staleness refs) and has no counterpart on a dashboard panel. These three writes
 * never touched the blob, so they are what the two surfaces can genuinely share.
 *
 * Deliberately **not** an invalidation policy: each write refreshes only what it provably changed,
 * and callers add their own extras, so one surface's Tight-plus-Plaid round trip is not imposed on
 * the other.
 */
export default function useStatementDocumentWrites(): StatementDocumentWrites {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const { data: member } = useMember();
    const memberId = member?.id;

    const assignAccount = useAssignStatementAccount();
    const reassignPeriods = useReassignStatementPeriods();
    const removeStatementTracking = useRemoveStatementTracking();

    /** Months removed per card and file, held until coverage stops listing them. */
    const removedMonthsRef = useRef(new Map<string, StatementPeriod[]>());

    /**
     * Refresh everything that reads which months are held, after a write that moved one.
     *
     * The two keys always travel together, which is why they are one call. Coverage is what the
     * cards and the Continue gate read. The verdicts are each document's own months, and nothing
     * else refetches them: the poll only runs while something is `processing`, and a settled
     * document is not — so a month that changed hands leaves the *other* document's verdict stale
     * with no way back.
     */
    const invalidateHeldMonths = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY });
        queryClient.invalidateQueries({ queryKey: STATEMENT_UPLOAD_STATUSES_QUERY_KEY });
    }, [queryClient]);

    /**
     * Log a failed write and tell the member about it — the two halves every catch here needs.
     *
     * `title` is load-bearing: RUM monitors key on these exact strings, so each caller passes its
     * own rather than sharing one.
     */
    const reportWriteFailure = useCallback(
        (title: string, error: unknown, message: string, context?: object) => {
            sendDatadogRumError(title, error, context);
            enqueueSnackbar(message, { variant: 'error', autoHideDuration: ERROR_SNACKBAR_DURATION_MS });
        },
        [enqueueSnackbar]
    );

    /**
     * Persist a member's month corrections. The rule — one statement per month, displacing whoever
     * held it — lives on the server; this owns the request, the refetches and the feedback.
     */
    const savePeriods = useCallback(
        async (
            assignments: StatementPeriodAssignment[],
            bankAccountId: string | null | undefined
        ): Promise<boolean> => {
            if (memberId == null) {
                return false;
            }
            try {
                // One request for the whole session. The server applies it as a set, so a file
                // displaced and then reassigned in the same edit keeps its row instead of being
                // deleted and rebuilt — which is what N single-document calls would do, one
                // committed middle state at a time.
                await reassignPeriods.mutateAsync({ memberId, assignments, bankAccountId });
            } catch (error) {
                // A refusal names what it refused, e.g. a statement already filed on another account.
                reportWriteFailure(
                    'Statement month reassignment failed',
                    error,
                    refusalMessage(error, SAVE_FAILED_MESSAGE),
                    { source: 'useStatementDocumentWrites.savePeriods' }
                );
                return false;
            }

            // This is the one correction path that genuinely moves months between documents.
            invalidateHeldMonths();
            enqueueSnackbar(PERIODS_SAVED_MESSAGE, {
                variant: 'success',
                autoHideDuration: SUCCESS_SNACKBAR_DURATION_MS,
            });
            return true;
        },
        [memberId, reassignPeriods, invalidateHeldMonths, reportWriteFailure, enqueueSnackbar]
    );

    /**
     * Take a statement off one card; its months on other cards stay, and the server deletes the
     * document once no account holds it. Resolves `false` rather than throwing, so a row's remove
     * button never swallows a rejection and onboarding knows to keep its step-blob entry.
     */
    const removeFromAccount = useCallback(
        async (documentUuid: string, account: RemovalAccount, period?: StatementPeriod): Promise<boolean> => {
            if (memberId == null) {
                // Nothing to release the months against. Every surface here loads the member
                // before it renders a file to remove, so this is a bug's fingerprint rather than a
                // state — reported, because the alternative is a button that does nothing at all.
                reportWriteFailure(
                    'Statement removal with no member loaded',
                    new Error('removeFromAccount called before the member resolved'),
                    DELETE_FAILED_MESSAGE,
                    { source: 'useStatementDocumentWrites.removeFromAccount', documentUuid }
                );
                return false;
            }

            try {
                await removeStatementTracking.mutateAsync({
                    memberId,
                    documentUuid,
                    bankAccountId: account.bank_account_id || null,
                    period,
                });
            } catch (error) {
                // A 409 is the server declining to give the month back — its transactions are
                // already in the books, or the bank delivered it. A decision rather than a failure
                // to retry, so the file stays and the server's reason is shown.
                const isRefused = isRefusal(error);
                reportWriteFailure(
                    'Statement removal from account failed',
                    error,
                    isRefused ? refusalMessage(error, DELETE_BLOCKED_MESSAGE) : DELETE_FAILED_MESSAGE,
                    { source: 'useStatementDocumentWrites.removeFromAccount', documentUuid, isRefused }
                );
                if (!isRefused) {
                    // A refusal removes nothing. Anything else may have committed before the transport
                    // failed, and a wasted refetch is cheaper than a false green.
                    invalidateHeldMonths();
                }
                return false;
            }

            if (period) {
                const key = `${account.bank_account_id}:${documentUuid}`;
                removedMonthsRef.current.set(key, [...(removedMonthsRef.current.get(key) ?? []), period]);
            }
            // Read before the refetch: afterwards a deleted file is in no list to be named from.
            const listed = cachedDocument(queryClient, documentUuid);
            // All three awaited, because the mutation refreshes nothing itself and each answers part
            // of what happened. The document lists drop a file the server deleted, and the filings
            // say what it still covers. Resolving only once coverage and the statuses are fresh is
            // also the contract: a caller reading them afterwards never sees this removal undone.
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: STATEMENT_COVERAGE_QUERY_KEY }),
                queryClient.invalidateQueries({ queryKey: STATEMENT_UPLOAD_STATUSES_QUERY_KEY }),
            ]);
            const statuses = queryClient.getQueryData<StatementUploadStatuses>([
                ...STATEMENT_UPLOAD_STATUSES_QUERY_KEY,
                memberId,
            ]);
            // Only a list that had the file and has refetched without it says the file is gone; a
            // failed refetch keeps the old list, and the notice then claims only the removal.
            const notice = removalNotice({
                removedFrom: account,
                period,
                fileName: listed?.file_name,
                isDeleted: listed !== undefined && cachedDocument(queryClient, documentUuid) === undefined,
                filings: statuses?.[documentUuid]?.statement_filings ?? [],
            });
            // An error's dwell: it describes something the member can no longer see.
            enqueueSnackbar(notice, { variant: 'info', autoHideDuration: ERROR_SNACKBAR_DURATION_MS });
            return true;
        },
        [memberId, removeStatementTracking, queryClient, invalidateHeldMonths, reportWriteFailure, enqueueSnackbar]
    );

    /**
     * Tell the server which account each new file was dropped on — the guarantee the modal's
     * verdicts rest on. See `useAssignStatementAccount` for what it buys.
     *
     * Announces nothing and invalidates nothing, because the two callers legitimately disagree
     * about both: onboarding resolves the account asynchronously anyway, while on the panel the
     * filing is what puts the file on a row. Per-document outcomes are returned so each can apply
     * its own policy.
     */
    const fileDocuments = useCallback(
        async (documentUuids: string[], bankAccountId: string | null | undefined): Promise<FileDocumentsResult> => {
            // Falsy, not `!= null`: an empty string is not an id either. Tight has assigned this
            // account nothing to file against, which coverage documents as a real state, not an error.
            if (memberId == null || !bankAccountId) {
                return { recorded: [], failed: [...documentUuids] };
            }
            const recorded: string[] = [];
            const failed: string[] = [];

            // Chained one after another rather than fired with `Promise.all`: each call makes a
            // live Tight round trip on the server to verify the id is really the member's.
            //
            // Each link swallows its own failure so the chain carries on to the rest of the batch.
            await documentUuids.reduce(async (previous, documentUuid) => {
                await previous;
                try {
                    await assignAccount.mutateAsync({ memberId, memberDocumentUuid: documentUuid, bankAccountId });
                    recorded.push(documentUuid);
                } catch (error) {
                    sendDatadogRumError('Statement account assignment failed', error, {
                        source: 'useStatementDocumentWrites.fileDocuments',
                        documentUuid,
                    });
                    failed.push(documentUuid);
                }
            }, Promise.resolve());

            return { recorded, failed };
        },
        [memberId, assignAccount]
    );

    const holdsMonthAfterRemoval = useCallback(
        (documentUuid: string, account: HeldAccount, removed?: StatementPeriod): boolean => {
            if (!removed) {
                return false;
            }
            const coverage = queryClient.getQueryData<MemberStatementCoverage>([
                ...STATEMENT_COVERAGE_QUERY_KEY,
                memberId ?? null,
            ]);
            const refetched = coverage?.accounts.find(
                (candidate) => candidate.bank_account_id === account.bank_account_id
            );
            const statuses = queryClient.getQueryData<StatementUploadStatuses>([
                ...STATEMENT_UPLOAD_STATUSES_QUERY_KEY,
                memberId,
            ]);
            const { monthsHere } = fileHoldings({
                status: statuses?.[documentUuid],
                bankAccountId: account.bank_account_id,
                rowsHere: groupHeldPeriods((refetched ?? account).held_periods)[documentUuid] ?? [],
            });
            const key = `${account.bank_account_id}:${documentUuid}`;
            // Pruned once nothing lists a month: from then on the reads say so themselves.
            const pending = (removedMonthsRef.current.get(key) ?? []).filter((period) =>
                includesPeriod(monthsHere, period)
            );
            removedMonthsRef.current.set(key, pending);
            return periodsNotHeld(monthsHere, [...pending, removed]).length > 0;
        },
        [queryClient, memberId]
    );

    return { savePeriods, removeFromAccount, holdsMonthAfterRemoval, fileDocuments };
}
